import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {
    StyledArrowRightCircleIcon,
    StyledDetailsTitle,
    StyledLockIcon,
    StyledSeeMeasureButton
} from '@pages/measure-details/style.ts'
import {Chip} from '@components/ui/chip/Chip.tsx'
import {Button} from '@components/ui/button/Button.tsx'
import {useTranslation} from 'react-i18next'
import {useParams} from 'react-router-dom'
import {useTheme} from 'styled-components'
import useWindowDimensions from '@hooks/useWindowDimensions.ts'
import {useEffect, useMemo, useState, useRef} from 'react'
import {Measure} from '@/features/measures/services/types.ts'
import {BrowserEmbedded} from '@/features/measures/components/browser-embedded/BrowserEmbedded.tsx'
import {useGetMeasureRawHtml} from '@/features/measure-details/services/useGetMeasureRawHtml.ts'
import {Spinner} from '@components/ui/spinner/Spinner.tsx'
import {CopyIcon, PrintIcon, DownloadIcon} from '@/components/ui/icon/Icon'
import {copyToClipboard} from '@/utilities/helpers'
import {toast} from 'react-hot-toast'
import {Tooltip} from '@components/ui/tooltip/Tooltip.tsx'
import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    ModalTitle
} from '@components/ui/modal-atoms/ModalAtoms.tsx'

type PendingAction = 'print' | 'copyLink' | 'download' | null
type CurrentAction = 'view' | 'print' | 'download' | null

export const MeasureDetailsHeader = ({measure}: {measure: Measure | undefined}) => {
    const {t} = useTranslation()
    const params = useParams()
    const theme = useTheme()
    const {width} = useWindowDimensions()
    const isMobile = useMemo(() => width < 768, [width])
    const [openEmbedded, setOpenEmbedded] = useState(false)
    const [pendingAction, setPendingAction] = useState<PendingAction>(null)
    const [currentAction, setCurrentAction] = useState<CurrentAction>(null)
    const measureQuery = useGetMeasureRawHtml()
    const printIframeRef = useRef<HTMLIFrameElement>(null)

    useEffect(() => {
        if (measureQuery.isSuccess && currentAction === 'view') {
            setOpenEmbedded(true)
        }
    }, [measureQuery.isSuccess, currentAction])

    useEffect(() => {
        if (measureQuery.isSuccess && currentAction === 'print' && measureQuery.data?.data) {
            // Create hidden iframe for printing
            const iframe = printIframeRef.current
            if (iframe) {
                const doc = iframe.contentDocument || iframe.contentWindow?.document
                if (doc) {
                    doc.open()
                    doc.write(measureQuery.data.data)
                    doc.close()

                    // Wait for iframe to load, then print
                    iframe.onload = () => {
                        iframe.contentWindow?.print()
                        // Reset after printing
                        setCurrentAction(null)
                        measureQuery.reset()
                    }
                }
            }
        }
    }, [measureQuery.isSuccess, currentAction, measureQuery.data])

    useEffect(() => {
        if (measureQuery.isSuccess && currentAction === 'download' && measureQuery.data?.data) {
            // Execute download
            const blob = new Blob([measureQuery.data.data], {type: 'text/html'})
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `provvedimento-${measure?.measureNumber || params.id}.html`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            window.URL.revokeObjectURL(url)

            // Reset after download
            setCurrentAction(null)
            measureQuery.reset()
        }
    }, [measureQuery.isSuccess, currentAction, measureQuery.data, measure?.measureNumber, params.id])

    const handleCopy = () => {
        copyToClipboard(measure?.appealNumber?.toString() || '')
        toast.success(t('commons:copy_success'))
    }

    const executeCopyLink = () => {
        const link = `${window.location.origin}/${params?.id}`
        copyToClipboard(link)
        toast.success(t('commons:copy_success'))
    }

    const executePrintRaw = () => {
        if (params?.id) {
            setCurrentAction('print')
            measureQuery.mutate(params.id)
        }
    }

    const executeDownload = () => {
        if (params?.id) {
            setCurrentAction('download')
            measureQuery.mutate(params.id)
        }
    }

    const handleCopyLink = () => {
        if (measure?.isOmissis) {
            setPendingAction('copyLink')
        } else {
            executeCopyLink()
        }
    }

    const handlePrintRaw = () => {
        if (measure?.isOmissis) {
            setPendingAction('print')
        } else {
            executePrintRaw()
        }
    }

    const handleDownload = () => {
        if (measure?.isOmissis) {
            setPendingAction('download')
        } else {
            executeDownload()
        }
    }

    const handleConfirmAction = () => {
        switch (pendingAction) {
            case 'print':
                executePrintRaw()
                break
            case 'copyLink':
                executeCopyLink()
                break
            case 'download':
                executeDownload()
                break
        }
        setPendingAction(null)
    }

    const handleCancelAction = () => {
        setPendingAction(null)
    }

    if (!isMobile) {
        return (
            <>
                {pendingAction && (
                    <>
                        <ModalOverlay />
                        <Modal width="400px">
                            <ModalHeader>
                                <ModalTitle>{t('measures:omissis_warning:title')}</ModalTitle>
                            </ModalHeader>
                            <ModalBody>
                                <p style={{whiteSpace: 'pre-line'}}>{t('measures:omissis_warning:message')}</p>
                            </ModalBody>
                            <ModalFooter>
                                <Button variant="ghost" onClick={handleCancelAction}>
                                    {t('commons:cancel')}
                                </Button>
                                <Button onClick={handleConfirmAction}>{t('measures:omissis_warning:proceed')}</Button>
                            </ModalFooter>
                        </Modal>
                    </>
                )}
                <Flexbox justify={'space-between'} align={'center'} fullWidth>
                    <Flexbox gap={4} align={'center'} justify={'flex-start'} direction={'row'}>
                        <StyledDetailsTitle>
                            {t('measure_details:title')} <strong>{measure?.measureNumber}</strong>{' '}
                            <CopyIcon fill="none" size={16} cursor={'pointer'} onClick={handleCopy} />
                        </StyledDetailsTitle>

                        {measure?.isOmissis && (
                            <Tooltip content={t('measures:table:omissis_tooltip')}>
                                <Chip
                                    text={t('measure_details:omissis')}
                                    icon={<StyledLockIcon />}
                                    iconPosition={'right'}
                                    bgColor={theme.palette.danger['50']}
                                    textColor={theme.palette.danger['700']}
                                />
                            </Tooltip>
                        )}
                    </Flexbox>

                    <Flexbox gap={2} align={'center'}>
                        <Tooltip content={t('measure_details:print_raw')} isOpenOnClickEnabled={false}>
                            <Button variant={'primaryLight'} onClick={handlePrintRaw}>
                                <PrintIcon size={20} />
                            </Button>
                        </Tooltip>

                        <Tooltip content={t('measure_details:copy_link')} isOpenOnClickEnabled={false}>
                            <Button variant={'primaryLight'} onClick={handleCopyLink}>
                                <CopyIcon size={20} fill="none" />
                            </Button>
                        </Tooltip>

                        <Tooltip content={t('measure_details:download')} isOpenOnClickEnabled={false}>
                            <Button variant={'primaryLight'} onClick={handleDownload}>
                                <DownloadIcon size={20} />
                            </Button>
                        </Tooltip>

                        <Button
                            variant={'primaryGhost'}
                            onClick={() => {
                                if (params?.id) {
                                    setCurrentAction('view')
                                    measureQuery.mutate(params.id)
                                }
                            }}
                        >
                            {t('measure_details:view_measure')}
                            {measureQuery.isPending ? <Spinner size={10} /> : <StyledArrowRightCircleIcon size={20} />}
                        </Button>
                    </Flexbox>

                    {measureQuery.isSuccess && (
                        <BrowserEmbedded
                            isOpen={openEmbedded}
                            onClose={() => {
                                setOpenEmbedded(false)
                                setCurrentAction(null)
                                measureQuery.reset()
                            }}
                            htmlContent={measureQuery?.data?.data}
                            isOmissis={measure?.isOmissis}
                            measureType={measure?.subType?.toLowerCase()}
                        />
                    )}

                    {/* Hidden iframe for printing */}
                    <iframe
                        ref={printIframeRef}
                        style={{
                            position: 'absolute',
                            width: '0',
                            height: '0',
                            border: 'none',
                            visibility: 'hidden'
                        }}
                        title="print-frame"
                    />
                </Flexbox>
            </>
        )
    } else {
        return (
            <>
                {pendingAction && (
                    <>
                        <ModalOverlay />
                        <Modal width="400px">
                            <ModalHeader>
                                <ModalTitle>{t('measures:omissis_warning:title')}</ModalTitle>
                            </ModalHeader>
                            <ModalBody>
                                <p style={{whiteSpace: 'pre-line'}}>{t('measures:omissis_warning:message')}</p>
                            </ModalBody>
                            <ModalFooter>
                                <Button variant="ghost" onClick={handleCancelAction}>
                                    {t('commons:cancel')}
                                </Button>
                                <Button onClick={handleConfirmAction}>{t('measures:omissis_warning:proceed')}</Button>
                            </ModalFooter>
                        </Modal>
                    </>
                )}
                <Flexbox gap={2} align={'flex-start'} fullWidth direction={'column'}>
                    <StyledDetailsTitle>
                        {t('measure_details:title')} <strong>{measure?.appealNumber}</strong>{' '}
                    </StyledDetailsTitle>
                    <Flexbox gap={2} align={'center'} justify={'flex-start'} style={{flexWrap: 'wrap'}}>
                        {measure?.isOmissis && (
                            <Chip
                                text={t('measure_details:omissis')}
                                icon={<StyledLockIcon />}
                                iconPosition={'right'}
                                bgColor={theme.palette.danger['50']}
                                textColor={theme.palette.danger['700']}
                            />
                        )}

                        <Button variant={'primaryLight'} onClick={handlePrintRaw}>
                            <PrintIcon size={20} />
                            {t('measure_details:print_raw')}
                        </Button>

                        <Button variant={'primaryLight'} onClick={handleCopyLink}>
                            <CopyIcon size={20} fill="none" />
                            {t('measure_details:copy_link')}
                        </Button>

                        <Button variant={'primaryLight'} onClick={handleDownload}>
                            <DownloadIcon size={20} />
                            {t('measure_details:download')}
                        </Button>

                        <StyledSeeMeasureButton
                            variant={'primaryGhost'}
                            onClick={() => {
                                if (params?.id) {
                                    setCurrentAction('view')
                                    measureQuery.mutate(params.id)
                                }
                            }}
                        >
                            {t('measure_details:view_measure')}
                            {measureQuery.isPending ? <Spinner size={10} /> : <StyledArrowRightCircleIcon size={20} />}
                        </StyledSeeMeasureButton>

                        {measureQuery.isSuccess && (
                            <BrowserEmbedded
                                isOpen={openEmbedded}
                                onClose={() => {
                                    setOpenEmbedded(false)
                                    setCurrentAction(null)
                                    measureQuery.reset()
                                }}
                                htmlContent={measureQuery?.data?.data}
                                isOmissis={measure?.isOmissis}
                                measureType={measure?.subType?.toLowerCase()}
                            />
                        )}
                    </Flexbox>

                    {/* Hidden iframe for printing */}
                    <iframe
                        ref={printIframeRef}
                        style={{
                            position: 'absolute',
                            width: '0',
                            height: '0',
                            border: 'none',
                            visibility: 'hidden'
                        }}
                        title="print-frame"
                    />
                </Flexbox>
            </>
        )
    }
}
